import { FeedPage } from '../feature/feed/list/FeedPage';
import { EditUserProfilePage } from '../feature/userProfile/EditUserProfilePage';
import { InboxPage } from '../feature/inbox/InboxPage';
import { UserDetailsPage } from '../feature/userDetails/UserDetailsPage';
import { UserVideosPage } from '../feature/userDetails/videos/UserVideosPage';
import { ContentDetailsPage } from '../feature/content/details/ContentDetailsPage';
import { UserSettingsPage } from '../feature/settings/list/UserSettingsPage';
import { SettingsCategoryPage } from '../feature/settings/category/SettingsCategoryPage';
import { PremiumPackagesPage } from '../feature/premiumPackages/list/PremiumPackagesPage';
import { CampaignsPage } from '../feature/campaigns/CampaignsPage';
import { LatestUsersPage } from '../feature/latestUsers/LatestUsersPage';
import { PremiumSubscriptionsPolicyPage } from '../feature/legal/PremiumSubscriptionsPolicyPage';
import { CoinsPolicyPage } from '../feature/legal/CoinsPolicyPage';
import { LegalDocumentsPage } from '../feature/legal/LegalDocumentsPage';
import { TermsOfUsePage } from '../feature/legal/TermsOfUsePage';
import { RewardsPolicyPage } from '../feature/legal/RewardsPolicyPage';
import { ExclusiveSubscriptionsPolicyPage } from '../feature/legal/ExclusiveSubscriptionsPolicyPage';
import { PrivacyPolicyPage } from '../feature/legal/PrivacyPolicyPage';
import { CommunityGuidelinesPage } from '../feature/legal/CommunityGuidelinesPage';
import { ContactPage } from '../feature/contact/ContactPage';
import { SupportPage } from '../feature/support/SupportPage';
import { SupportItemPage } from '../feature/support/SupportItemPage';
import { AboutPage } from '../feature/about/AboutPage';
import { DeleteAccountPage } from '../feature/deleteAccount/DeleteAccountPage';
import { WalletPage } from '../feature/wallet/WalletPage';
import { SearchPage } from '../feature/search/SearchPage';
import { ContentTagsPage } from '../feature/contentTag/contentTagsPage/ContentTagsPage';
import { UnlockedVideosPage } from '../feature/unlockedContent/UnlockedVideosPage';
import { LikedVideosPage } from '../feature/likedVideos/LikedVideosPage';
import { SavedContentPage } from '../feature/savedContent/SavedContentPage';
import { SubscriptionsPage } from '../feature/subscriptions/SubscriptionsPage';
import { SubscribersPage } from '../feature/subscriptions/SubscribersPage';
import { SavedUsersPage } from '../feature/savedUsers/SavedUsersPage';
import { MockChatPage } from '../feature/mockChat/MockChatPage';
import { ChatPage } from '../feature/chat/ChatPage';
import { CommunityPage } from '../feature/community/CommunityPage';
import { CommunityMediaPage } from '../feature/community/media/CommunityMediaPage';
import { RoadmapPage } from '../feature/roadmap/RoadmapPage';
import { AddRoadmapPage } from '../feature/roadmap/add/AddRoadmapPage';
import { MediaDetailsPage } from '../feature/chat/media/MediaDetailsPage';
import { VideoCallPage } from '../feature/videoCall/VideoCallPage';
import { LiveStreamingPage } from '../feature/liveStreaming/LiveStreamingPage';
import { DefaultMediaPage } from '../feature/defaultMedia/DefaultMediaPage';
import { UsersFeedbackDetailsPage } from '../feature/feedback/details/UsersFeedbackDetailsPage';
import { UsersFeedbackPage } from '../feature/feedback/list/UsersFeedbackPage';
import { FeedbackPage } from '../feature/feedback/add/FeedbackPage';
import type { l10n } from '@/presentation/locale/l10n';

/**
 * Base route configuration interface
 */
interface BaseRouteConfig {
  path: string;
  element: React.ComponentType;
  /** Whether this route should be kept in cache when navigating away */
  persistCache: boolean;
}

/**
 * Main navigation route configuration (shown in TopBar)
 */
export interface MainNavigationRouteConfig extends BaseRouteConfig {
  /** Function to get the localized label for this route */
  label: (l10n: l10n) => string;
}

/**
 * Detail/secondary route configuration
 */
export interface DetailRouteConfig extends BaseRouteConfig { }

/**
 * Public route configuration - accessible without authentication
 */
export interface PublicRouteConfig extends BaseRouteConfig { }

/**
 * Public routes - accessible without authentication, no auth redirect
 */
export const PUBLIC_ROUTES: PublicRouteConfig[] = [
  {
    path: 'legal',
    element: LegalDocumentsPage,
    persistCache: false,
  },
  {
    path: 'legal/premium-subscriptions-policy',
    element: PremiumSubscriptionsPolicyPage,
    persistCache: false,
  },
  {
    path: 'legal/coins-policy',
    element: CoinsPolicyPage,
    persistCache: false,
  },
  {
    path: 'legal/terms-of-use',
    element: TermsOfUsePage,
    persistCache: false,
  },
  {
    path: 'legal/rewards-policy',
    element: RewardsPolicyPage,
    persistCache: false,
  },
  {
    path: 'legal/exclusive-subscriptions-policy',
    element: ExclusiveSubscriptionsPolicyPage,
    persistCache: false,
  },
  {
    path: 'legal/privacy-policy',
    element: PrivacyPolicyPage,
    persistCache: false,
  },
  {
    path: 'legal/community-guidelines',
    element: CommunityGuidelinesPage,
    persistCache: false,
  },
  {
    path: 'contact',
    element: ContactPage,
    persistCache: false,
  },
  {
    path: 'support',
    element: SupportPage,
    persistCache: false,
  },
  {
    path: 'support/:id',
    element: SupportItemPage,
    persistCache: false,
  },
  {
    path: 'about',
    element: AboutPage,
    persistCache: false,
  },
  // `delete-account` — the public, Play-compliant account-deletion page.
  // Registered under PUBLIC_ROUTES (not wrapped in `PublicRoute`) so it is
  // reachable signed-in or not and does NOT redirect authenticated users
  // away — the flow needs an authenticated session to call the delete CF.
  {
    path: 'delete-account',
    element: DeleteAccountPage,
    persistCache: false,
  },
];

/**
 * Check if a pathname matches one of the configured PUBLIC_ROUTES.
 *
 * Used by `ProtectedRoute` so public routes (the legal pages) can be mounted
 * inside the same protected-routes subtree (and therefore share the same
 * `KeepAliveOutlet` cache) without redirecting unauthenticated users to
 * `/login`. The route table itself is the single source of truth — adding a
 * new public route does not require touching this predicate.
 */
export const isPublicRoutePath = (pathname: string): boolean => {
  const normalized = pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  return PUBLIC_ROUTES.some((route) => route.path === normalized);
};

/**
 * Main navigation routes - shown in TopBar and nested within MainScreen
 * These routes are always cached and share the MainScreen layout with TopBar
 * They use screenKey for consistent caching behavior (singleton per route)
 */
export const MAIN_NAVIGATION_ROUTES: MainNavigationRouteConfig[] = [
  {
    path: '/',
    element: FeedPage,
    persistCache: true,
    label: (l10n) => l10n.routes.home,
  },
  {
    path: 'inbox',
    element: InboxPage,
    persistCache: true,
    label: (l10n) => l10n.routes.inbox,
  },
];

/**
 * Detail routes - not shown in TopBar, rendered outside MainScreen
 * These routes have their own layout without TopBar
 * All detail routes use screenKey for consistent caching behavior
 */
export const DETAIL_ROUTES: DetailRouteConfig[] = [
  {
    path: 'profile/edit',
    element: EditUserProfilePage,
    persistCache: false,
  },
  // `/chat/:peerId` — must precede the `':userId'` catch-all below so the
  // literal `chat/...` segment is not matched as a user id. React Router
  // matches static path segments before dynamic ones, but ordering this
  // entry above the catch-all keeps the precedence intent explicit.
  {
    path: 'chat/:peerId',
    element: ChatPage,
    persistCache: false,
  },
  // `/community/:userId` — the per-creator community wall. Must precede the
  // `':userId'` catch-all below so the literal `community/...` segment is not
  // matched as a user id. React Router matches static path segments before
  // dynamic ones, but ordering this entry above the catch-all keeps the
  // precedence intent explicit. In-app nav forwards the full `UserEntity` via
  // `location.state` (see `Navigator.navigateToCommunity`); deep-link / refresh
  // rehydrates from the `:userId` param (Task 13).
  {
    path: 'community/:userId',
    element: CommunityPage,
    persistCache: false,
  },
  // `/community/media/:id` (`:id` = community message document id) — the
  // directly-routable community-media (video) player. Its three-segment path
  // cannot collide with `community/:userId` (two segments) or the `':userId'`
  // catch-all, but grouping it with the other community route keeps the intent
  // explicit. The message is passed via `location.state` (a pasted URL has no
  // args → error, never a by-id fetch — the same anti-deep-link behaviour as
  // `chat/media/:id`). The unique `messageId` keeps the per-instance route cache
  // working.
  {
    path: 'community/media/:id',
    element: CommunityMediaPage,
    persistCache: false,
  },
  // `/chat/media/:id` (`:id` = chat message `documentId`) — the directly-
  // routable chat-media viewer. Must precede the `':userId'` catch-all so the
  // literal `chat/media/...` segment is not matched as a user id. The message +
  // peer key are passed via `location.state` (a pasted URL has no args → error,
  // never a by-id fetch — spec point 7 / consideration B). The unique
  // `documentId` keeps the per-instance route cache working.
  {
    path: 'chat/media/:id',
    element: MediaDetailsPage,
    persistCache: false,
  },
  // `/video-call/:id` (`:id` = the call session `callMessageId`) — the 1:1 chat
  // video-call screen. Must precede the `':userId'` catch-all so the literal
  // `video-call/...` segment is not matched as a user id. The full call context
  // is passed via `location.state` (a pasted URL has no args → error, never a
  // by-id fetch — like `chat/media/:id`). `persistCache: false` so the call
  // surface is dropped from the route cache on POP — a finished call must never
  // be restored from cache. The unique `callMessageId` keeps the per-instance
  // route cache working (see `Navigator.navigateToVideoCall`).
  {
    path: 'video-call/:id',
    element: VideoCallPage,
    persistCache: false,
  },
  // `/live/:id` (`:id` = the live message `liveStreamingMessageId`) — the
  // community live-session screen (broadcaster + audience). Must precede the
  // `':userId'` catch-all so the literal `live/...` segment is not matched as a
  // user id. The full live context is passed via `location.state` (a pasted URL
  // has no args → error, never a by-id fetch — like `video-call/:id`).
  // `persistCache: false` so the live surface is dropped from the route cache on
  // POP — a finished live session must never be restored from cache. The unique
  // `liveStreamingMessageId` keeps the per-instance route cache working (see
  // `Navigator.navigateToLiveStreaming`).
  {
    path: 'live/:id',
    element: LiveStreamingPage,
    persistCache: false,
  },
  {
    path: ':userId',
    element: UserDetailsPage,
    persistCache: false,
  },
  {
    path: ':userId/videos',
    element: UserVideosPage,
    persistCache: false,
  },
  {
    path: 'content/:userMediaId',
    element: ContentDetailsPage,
    persistCache: false,
  },
  // `default-media/:id` — web-only full-viewport viewer for media on the
  // authenticated (but unencrypted) cloud default storage bucket — not public —
  // hence the `default-media` name (not the old `public-content`), and distinct
  // from the chat-media viewer at `chat/media/:id`. Grouped with the other
  // static detail routes (alongside `content/:userMediaId`) so React Router
  // (which matches static segments before the `:userId` catch-all) routes it to
  // `DefaultMediaPage` rather than user-details. The `:id` segment is a random
  // cache-busting id — the real payload is the `url` in `location.state`
  // (see `Navigator.navigateToDefaultMedia`).
  {
    path: 'default-media/:id',
    element: DefaultMediaPage,
    persistCache: false,
  },
  // `admin/users-feedback` — admin users-feedback list. Namespaced under
  // `admin/` alongside the other admin-only routes; its multi-segment path
  // cannot collide with the single-segment `:userId` catch-all.
  {
    path: 'admin/users-feedback',
    element: UsersFeedbackPage,
    persistCache: false,
  },
  // `admin/users-feedback/:id` — admin users-feedback details. The `:id` is
  // `feedback.id`; the real payload is the `feedback` in `location.state`
  // (see `Navigator.navigateToUsersFeedbackDetails`).
  {
    path: 'admin/users-feedback/:id',
    element: UsersFeedbackDetailsPage,
    persistCache: false,
  },
  {
    path: 'premium-packages',
    element: PremiumPackagesPage,
    persistCache: false,
  },
  {
    path: 'campaigns',
    element: CampaignsPage,
    persistCache: false,
  },
  // `feedback` — the "add feedback" write flow (`FeedbackPage`). Single static
  // segment; grouped with the other single-segment static routes (`campaigns` /
  // `premium-packages`) so React Router matches it before the `:userId`
  // catch-all above rather than treating `feedback` as a user id. Web mirror of
  // Flutter's `InAppFeedbackScreen` (`in_app_feedback_screen.dart`).
  {
    path: 'feedback',
    element: FeedbackPage,
    persistCache: false,
  },
  {
    path: 'admin/latest-users',
    element: LatestUsersPage,
    persistCache: false,
  },
  {
    path: 'settings',
    element: UserSettingsPage,
    persistCache: false,
  },
  {
    path: 'settings/:id',
    element: SettingsCategoryPage,
    persistCache: false,
  },
  {
    path: 'wallet',
    element: WalletPage,
    persistCache: false,
  },
  {
    path: 'search',
    element: SearchPage,
    persistCache: false,
  },
  {
    path: 'hashtag/:tagName',
    element: ContentTagsPage,
    persistCache: false,
  },
  {
    path: 'label/:tagName',
    element: ContentTagsPage,
    persistCache: false,
  },
  {
    path: 'unlocked',
    element: UnlockedVideosPage,
    persistCache: false,
  },
  {
    path: 'liked',
    element: LikedVideosPage,
    persistCache: false,
  },
  {
    path: 'watch-later',
    element: SavedContentPage,
    persistCache: false,
  },
  {
    path: 'ideas',
    element: RoadmapPage,
    persistCache: false,
  },
  {
    path: 'ideas/add',
    element: AddRoadmapPage,
    persistCache: false,
  },
  {
    path: 'subscriptions',
    element: SubscriptionsPage,
    persistCache: false,
  },
  {
    path: 'subscribers',
    element: SubscribersPage,
    persistCache: false,
  },
  {
    path: 'saved-users',
    element: SavedUsersPage,
    persistCache: false,
  },
  // Dev-only harness for `VirtualList(reversed)`. Intentionally absent
  // from `MAIN_NAVIGATION_ROUTES` — must not appear in the TopBar.
  {
    path: 'mock-chat',
    element: MockChatPage,
    persistCache: false,
  },
];
